// Client-side password policy helpers used for UX feedback in the password
// reset and change forms. These mirror the authoritative server-side policy in
// charisma-aws-webservice/src/helpers/passwordPolicy.ts. The server remains the
// authoritative enforcement point; these checks only drive live feedback.

export const MIN_LENGTH = 12;
export const REQUIRED_CATEGORY_COUNT = 3;

export type PasswordRule = {
  label: string;
  isMet: (password: string) => boolean;
};

export const CATEGORY_RULES: PasswordRule[] = [
  { label: "An uppercase letter (A-Z)", isMet: pw => /[A-Z]/.test(pw) },
  { label: "A lowercase letter (a-z)", isMet: pw => /[a-z]/.test(pw) },
  { label: "A number (0-9)", isMet: pw => /[0-9]/.test(pw) },
  { label: "A special character", isMet: pw => /[^A-Za-z0-9]/.test(pw) }
];

/**
 * Count how many of the four complexity categories the password satisfies.
 */
export const countCategories = (password: string): number =>
  CATEGORY_RULES.filter(rule => rule.isMet(password)).length;

/**
 * Minimum length a name/username token must have to be checked. Tokens shorter
 * than this are ignored so very short names don't block a large share of
 * otherwise-valid passwords.
 */
export const MIN_NAME_TOKEN_LENGTH = 3;

/**
 * Determine whether the password contains the supplied token in its entirety
 * (case-insensitive). Mirrors the backend `containsNameToken`. Only whole
 * tokens are matched — substrings/fragments of a name are NOT rejected, so
 * common letter sequences don't cause false positives. Tokens shorter than
 * MIN_NAME_TOKEN_LENGTH are skipped.
 */
export const containsNameToken = (
  password: string,
  token: string | undefined | null
): boolean => {
  if (!password || !token || token.length < MIN_NAME_TOKEN_LENGTH) {
    return false;
  }

  return password.toLowerCase().includes(token.toLowerCase());
};

export type IdentityFields = {
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

/**
 * Whether the password contains the full username or a full name part.
 */
export const containsIdentity = (
  password: string,
  { username, firstName, lastName }: IdentityFields
): boolean =>
  containsNameToken(password, username) ||
  containsNameToken(password, firstName) ||
  containsNameToken(password, lastName);

/**
 * Validate a password against the client-side length, complexity, and identity
 * rules. Returns an error message string when invalid, or `true` when valid,
 * matching the react-hook-form `validate` contract.
 *
 * The optional `identity` argument enables the "must not contain your username
 * or name" check. When omitted (identity not yet loaded), that rule is skipped
 * client-side; the server still enforces it authoritatively.
 */
export const validatePassword = (
  passwordValue: string,
  identity: IdentityFields = {}
): string | true => {
  if (!passwordValue) {
    return "New password is required";
  }
  if (passwordValue.length < MIN_LENGTH) {
    return `Password must be at least ${MIN_LENGTH} characters.`;
  }
  if (countCategories(passwordValue) < REQUIRED_CATEGORY_COUNT) {
    return `Password must contain characters from at least ${REQUIRED_CATEGORY_COUNT} of: uppercase, lowercase, digits, special characters.`;
  }
  if (containsIdentity(passwordValue, identity)) {
    return "Password must not contain your username or name.";
  }
  return true;
};
